import { useEffect, useState } from 'react';

/**
 * Viewport-state hook aligned to the project's MUI breakpoints (sm = 600 px,
 * md = 960 px). Use this instead of ad-hoc `window.matchMedia` checks so the
 * JS-side state stays in sync with the CSS media queries from
 * `assets/style/breakpointHelpers.ts` (`down('sm')`, `down('md')`).
 *
 * Tiers:
 *  - `isSm`              : ≤ 599.95 px  — small phones (320–599 px)
 *  - `isMd`              : ≤ 959.95 px  — medium phones (also true for `isSm`)
 *  - `isTabletPortrait`  : 768 px – 959.95 px  — iPads in portrait, large phones
 *  - `isTouchDevice`     : capability flag, not a viewport flag
 */

const SM_QUERY = '(max-width: 599.95px)';
const MD_QUERY = '(max-width: 959.95px)';
const TABLET_PORTRAIT_QUERY = '(min-width: 768px) and (max-width: 959.95px)';

const isBrowser = typeof window !== 'undefined';

const matches = (q: string): boolean => (isBrowser ? window.matchMedia(q).matches : false);

export interface ViewportState {
	width: number;
	isSm: boolean;
	isMd: boolean;
	isTabletPortrait: boolean;
	isTouchDevice: boolean;
}

const buildSnapshot = (): ViewportState => ({
	width: isBrowser ? window.innerWidth : 0,
	isSm: matches(SM_QUERY),
	isMd: matches(MD_QUERY),
	isTabletPortrait: matches(TABLET_PORTRAIT_QUERY),
	isTouchDevice: isBrowser && ('ontouchstart' in window || navigator.maxTouchPoints > 0),
});

export const useViewport = (): ViewportState => {
	const [state, setState] = useState<ViewportState>(buildSnapshot);

	useEffect(() => {
		if (!isBrowser) return;

		const update = () => setState(buildSnapshot());

		const queries = [SM_QUERY, MD_QUERY, TABLET_PORTRAIT_QUERY].map((q) => window.matchMedia(q));
		queries.forEach((mql) => mql.addEventListener('change', update));
		window.addEventListener('resize', update);

		return () => {
			queries.forEach((mql) => mql.removeEventListener('change', update));
			window.removeEventListener('resize', update);
		};
	}, []);

	return state;
};

export default useViewport;
